import React, { useState, useEffect } from "react";
import FAQ from "../components/Faq";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/cartContext"; // Assuming you have a cart context
import { useNavigate } from "react-router-dom";
import axios from "axios"; // To send API requests
import { useUser } from "../context/userContext"; // Import the useUser hook

const Checkout = () => {
  const { cartItems, calculateTotal } = useCart(); // Get cart items and total
  const { user } = useUser(); // Get user data from auth context
  const [deliveryFee] = useState(10.0); // Static delivery fee
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    city: "Zurich", // Default selected city
    address: "",
    postalCode: "",
    phone: "",
    notes: "",
  });
  const navigate = useNavigate(); // To navigate to payment page
  const [additionalInfo, setAdditionalInfo] = useState(null); // Initialize with null
  const [loading, setLoading] = useState(true); // New loading state
  const total = calculateTotal(); // Subtotal from the cart
  const finalTotal = total + deliveryFee; // Final total

  useEffect(() => {
    const fetchAdditionalInfo = async () => {
      if (!user || !user.id) {
        console.error("User is not authenticated");
        setLoading(false); // Stop loading if user is not authenticated
        return;
      }

      try {
        const response = await axios.get(
          `https://api.theonlysale.com/api/additional-info/get/${user.id}`
        );
        console.log("Response data:", response.data); // Log response data
        if (response.data) {
          setAdditionalInfo(response.data);
        } else {
          setAdditionalInfo(null); // Set to null if no additional info is found
        }
      } catch (error) {
        console.error("Failed to fetch additional info", error);
        setAdditionalInfo(null); // Ensure null on error
      } finally {
        setLoading(false); // Stop loading when done
      }
    };

    fetchAdditionalInfo();
  }, [user]); // Run whenever user changes

  useEffect(() => {
    if (user) {
      // Use optional chaining to safely access nested values
      setFormData({
        name: user.name || "",
        email: user.email || "",
        city: additionalInfo?.data.city || "Zurich", // Default to Zurich if no city is provided
        address: additionalInfo?.data.address || "",
        postalCode: additionalInfo?.data.postal_code || "",
        phone: user.phone_number || "",
        notes: "", // Start with an empty note field
      });
    }
  }, [user, additionalInfo]); // Only run when user or additionalInfo changes

  // Handle form input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    console.log("Form Data Updated:", { ...formData, [id]: value });
  };

  // Handle payment process and submit order data
  const handlePayment = async (e) => {
    e.preventDefault();
  
    // Prepare order data
    const orderData = {
      ...formData,
      totalAmount: finalTotal,
      status: "Pending",
      paymentIntentId: "A", // Add paymentIntentId as null initially or you can replace this with a value if you have one
      products: cartItems.map((item) => ({
        id: item.id,
        quantity: item.quantity || 1, // Set to 1 if quantity is not defined
      })),
    };
  
    try {
      // Send order data to backend
      const response = await axios.post(
        "https://api.theonlysale.com/api/order/create",
        orderData
      );
  
      // Inside handlePayment function in Checkout.jsx
      if (response.status === 201) {
        const orderId = response.data.order.order_id;
        // Navigate to the payment page with the orderId and total amount
        navigate(`/payment?orderId=${orderId}&amount=${finalTotal}`);
      }
    } catch (error) {
      console.error("Error creating order:", error);
      if (error.response) {
        console.error("Error Response Data:", error.response.data);
        console.error("Error Response Status:", error.response.status);
      }
    }
  };
  
  // Show a loading message if data is being fetched
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Header />
      <section className="bg-white py-8 antialiased dark:bg-gray-900 md:py-16">
        <form
          onSubmit={handlePayment}
          className="mx-auto max-w-screen-xl px-4 2xl:px-0"
        >
          <div className="mt-6 lg:flex lg:items-start lg:gap-12 xl:gap-16">
            {/* Delivery Details Form */}
            <div className="min-w-0 flex-1 space-y-8">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Delivery Details
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {/* Name Input */}
                  <div>
                    <label
                      htmlFor="name"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Your Name"
                      required
                    />
                  </div>

                  {/* Email Input */}
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Your Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="you@example.com"
                      required
                    />
                  </div>

                  {/* City Input */}
                  <div>
                    <label
                      htmlFor="city"
                      className="block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      City
                    </label>
                    <select
                      id="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      required
                    >
                      <option value="Zurich">Zurich</option>
                      <option value="Geneva">Geneva</option>
                      <option value="Basel">Basel</option>
                      <option value="Bern">Bern</option>
                      <option value="Lucerne">Lucerne</option>
                    </select>
                  </div>

                  {/* Address Input */}
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="address"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Street Address
                    </label>
                    <input
                      type="text"
                      id="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="1234 Elm Street"
                      required
                    />
                  </div>

                  {/* Postal Code Input */}
                  <div>
                    <label
                      htmlFor="postalCode"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Postal Code
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="8001"
                      required
                    />
                  </div>

                  {/* Phone Input */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Phone Number
                    </label>
                    <input
                      type="text"
                      id="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="+41 76 123 4567"
                      required
                    />
                  </div>

                  {/* Additional Notes */}
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="notes"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Additional Notes
                    </label>
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="3"
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Any specific instructions or delivery preferences."
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="mt-6 w-full space-y-6 lg:mt-0 lg:max-w-xs xl:max-w-md">
              <div className="flow-root">
                <div className="-my-3 divide-y divide-gray-200 dark:divide-gray-800">
                  <dl className="flex items-center justify-between gap-4 py-3">
                    <dt className="text-base font-normal text-gray-500 dark:text-gray-400">
                      Subtotal
                    </dt>
                    <dd className="text-base font-medium text-gray-900 dark:text-white">
                      ${total.toFixed(2)}
                    </dd>
                  </dl>
                  <dl className="flex items-center justify-between gap-4 py-3">
                    <dt className="text-base font-normal text-gray-500 dark:text-gray-400">
                      Delivery Fee
                    </dt>
                    <dd className="text-base font-medium text-gray-900 dark:text-white">
                      ${deliveryFee.toFixed(2)}
                    </dd>
                  </dl>
                  <dl className="flex items-center justify-between gap-4 py-3">
                    <dt className="text-base font-bold text-gray-900 dark:text-white">
                      Total
                    </dt>
                    <dd className="text-base font-bold text-gray-900 dark:text-white">
                      ${finalTotal.toFixed(2)}
                    </dd>
                  </dl>
                </div>
              </div>

              {/* Proceed to Payment Button */}
              <div className="space-y-3">
                <button
                  type="submit"
                  className="flex w-full items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 px-5 py-2.5 text-sm font-medium text-white dark:bg-primary-600 dark:hover:bg-primary-700"
                >
                  Proceed to Payment
                </button>
              </div>
            </div>
          </div>
        </form>
      </section>
      <Footer />
    </>
  );
};

export default Checkout;
